import { Input, Select, TextArea } from '@components'
import { SUPPORTED_LANGUAGES } from '@constants'
import { useEdges, useNodes } from '@xyflow/react'
import React from 'react'
import { Controller, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { EmailSendNodeConfig } from '../nodes/types'
import { EmailRecipientsField } from './EmailRecipientsField'
import { optionIncludesValue } from './formOptionUtils'
import { TabButton, TabGroup } from './segmentedTabs.styles'
import { resolveUpstreamTextSource } from './upstreamTextSource'
import { useWorkflowConfigForm } from './useWorkflowConfigForm'

const SUBJECT_MAX_LENGTH = 255
const MESSAGE_MAX_LENGTH = 5000

interface Props {
  nodeId: string
  config: EmailSendNodeConfig
  onChange: (config: EmailSendNodeConfig) => void
}

export const EmailSendConfigForm: React.FC<Props> = ({ nodeId, config, onChange }) => {
  const [t] = useTranslation()
  const edges = useEdges()
  const nodes = useNodes()
  const textSource = resolveUpstreamTextSource(nodeId, nodes, edges)
  const upstreamStep = textSource ? t(textSource.labelKey) : null

  const { control, pushConfig, setValue, trigger } = useWorkflowConfigForm<EmailSendNodeConfig>({
    defaultValues: {
      ...config,
      recipients: config.recipients ?? [],
      recipient_tags: config.recipient_tags ?? []
    },
    onChange
  })
  const messageSource = useWatch({ control, name: 'message_source' })
  const message = useWatch({ control, name: 'message' }) ?? ''
  const recipientTags = useWatch({ control, name: 'recipient_tags' }) ?? []

  return (
    <>
      <Controller
        name="recipients"
        control={control}
        rules={{
          // Read tags from form values: the closure is stale when trigger() runs
          // right after setValue('recipient_tags').
          validate: (value, formValues) => {
            const addresses = value ?? []
            if (!addresses.length && !(formValues.recipient_tags ?? []).length) {
              return t('workflows.config.emailSend.recipientsRequired')
            }
            return true
          }
        }}
        render={({ field, fieldState }) => (
          <FieldStack>
            <EmailRecipientsField
              inputId={`workflow-send-to-${nodeId}`}
              recipients={field.value ?? []}
              recipientTags={recipientTags}
              error={fieldState.error?.message}
              onChangeRecipients={recipients => {
                field.onChange(recipients)
                pushConfig()
              }}
              onChangeTags={tags => {
                setValue('recipient_tags', tags, { shouldValidate: true })
                void trigger('recipients')
                pushConfig()
              }}
            />
          </FieldStack>
        )}
      />
      <Controller
        name="subject"
        control={control}
        rules={{
          validate: value =>
            Boolean(value?.trim()) || t('workflows.config.emailSend.subjectRequired'),
          maxLength: {
            value: SUBJECT_MAX_LENGTH,
            message: t('workflows.config.emailSend.subjectTooLong', { max: SUBJECT_MAX_LENGTH })
          }
        }}
        render={({ field, fieldState }) => (
          <Input
            id={`workflow-email-subject-${nodeId}`}
            name={`workflow-email-subject-${nodeId}`}
            label={t('workflows.config.emailSend.subject')}
            required
            maxLength={SUBJECT_MAX_LENGTH}
            value={field.value}
            ref={field.ref}
            onBlur={field.onBlur}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              field.onChange(event.target.value)
              pushConfig()
            }}
            errors={fieldState.error}
          />
        )}
      />
      <Controller
        name="language"
        control={control}
        render={({ field, fieldState }) => (
          <Select
            id={`workflow-email-language-${nodeId}`}
            name={`workflow-email-language-${nodeId}`}
            label={t('workflows.config.emailSend.language')}
            info={t('workflows.config.emailSend.languageHint')}
            value={field.value}
            errors={fieldState.error}
            onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
              field.onChange(event.target.value)
              pushConfig()
            }}
            onBlur={field.onBlur}
          >
            {SUPPORTED_LANGUAGES.map(({ value, labelKey }) => (
              <option key={value} value={value}>
                {t(labelKey)}
              </option>
            ))}
            {!optionIncludesValue(SUPPORTED_LANGUAGES, field.value) && field.value && (
              <option value={field.value}>{field.value}</option>
            )}
          </Select>
        )}
      />
      <FieldStack>
        <MessageHeader>
          <FieldLabel>{t('workflows.config.emailSend.message')}</FieldLabel>
          <Controller
            name="message_source"
            control={control}
            render={({ field }) => (
              <TabGroup role="tablist">
                {(['upstream', 'custom'] as const).map(source => (
                  <TabButton
                    key={source}
                    type="button"
                    role="tab"
                    aria-selected={field.value === source}
                    $selected={field.value === source}
                    onClick={() => {
                      field.onChange(source)
                      pushConfig()
                    }}
                  >
                    {source === 'upstream'
                      ? t('workflows.config.emailSend.sourceUpstream')
                      : t('workflows.config.emailSend.sourceCustom')}
                  </TabButton>
                ))}
              </TabGroup>
            )}
          />
        </MessageHeader>
        {messageSource === 'custom' ? (
          <Controller
            name="message"
            control={control}
            rules={{
              validate: value =>
                Boolean(value?.trim()) || t('workflows.config.emailSend.messageRequired'),
              maxLength: {
                value: MESSAGE_MAX_LENGTH,
                message: t('workflows.config.emailSend.messageTooLong', {
                  max: MESSAGE_MAX_LENGTH
                })
              }
            }}
            render={({ field, fieldState }) => (
              <>
                <MessageTextArea
                  id={`workflow-email-message-${nodeId}`}
                  name={`workflow-email-message-${nodeId}`}
                  rows={6}
                  maxLength={MESSAGE_MAX_LENGTH}
                  placeholder={t('workflows.config.emailSend.messagePlaceholder')}
                  value={field.value}
                  ref={field.ref}
                  onBlur={field.onBlur}
                  onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
                    field.onChange(event.target.value)
                    pushConfig()
                  }}
                  errors={fieldState.error}
                />
                <MessageFooter>
                  <HelperText>{t('workflows.config.emailSend.messageHint')}</HelperText>
                  <Counter>{`${message.length} / ${MESSAGE_MAX_LENGTH}`}</Counter>
                </MessageFooter>
              </>
            )}
          />
        ) : (
          <HelperText>
            {upstreamStep
              ? t('workflows.config.emailSend.upstreamHint', { step: upstreamStep })
              : t('workflows.config.emailSend.messageFromFlow')}
          </HelperText>
        )}
      </FieldStack>
    </>
  )
}

const FieldStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs}rem;
`

const FieldLabel = styled.label`
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.metalGray};
`

const HelperText = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  color: ${({ theme }) => theme.colors.iconGray};
  line-height: 1.4;
`

const MessageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

const MessageTextArea = styled(TextArea)`
  min-height: 100px;
  max-height: 300px;
  resize: vertical;
  line-height: 1.5;
`

const MessageFooter = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

const Counter = styled.span`
  flex-shrink: 0;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  color: ${({ theme }) => theme.colors.iconGray};
  font-variant-numeric: tabular-nums;
`
